
import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Loader2, FileText, CheckCircle } from 'lucide-react';
import DocumentsTable from './DocumentsTable';
import documentsApi from '../../api/documentsApi';
import { DocumentMetadata } from '../../api/types';
import { useBusiness } from '../../context/BusinessContext';

const Documents: React.FC = () => {
    // State management for modals
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(false);

    // Data State
    const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Form State
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [docType, setDocType] = useState('Profit & Loss Statements');

    // Business State using Context
    const { selected: business } = useBusiness();

    // Fetch Documents
    useEffect(() => {
        if (!business?.slug) return;

        const fetchDocs = async () => {
            setLoadingDocs(true);
            try {
                const docs = await documentsApi.getDocuments(business.slug);
                setDocuments(docs);
            } catch (error) {
                console.error("Error fetching documents:", error);
            } finally {
                setLoadingDocs(false);
            }
        };

        fetchDocs();
    }, [business?.slug, refreshTrigger]);

    // Handler for "Select files" button (mock trigger, real input would be better but keeping simple)
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            setIsUploadModalOpen(true);
        }
    };

    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    const handleUpload = async () => {
        if (!selectedFile || !business?.slug) return;

        setUploadProgress(true);
        try {
            await documentsApi.uploadDocument(selectedFile, business.slug, docType);
            setUploadProgress(false);
            setIsUploadModalOpen(false);
            setIsSuccessModalOpen(true);

            // Allow success modal to show for a bit, then refresh list
            setTimeout(() => {
                setIsSuccessModalOpen(false);
                setRefreshTrigger(prev => prev + 1);
                setSelectedFile(null);
            }, 1500);

        } catch (error) {
            console.error("Upload failed", error);
            setUploadProgress(false);
            alert("Upload failed. Please try again.");
        }
    };

    return (
        <div className="flex flex-col gap-8 overflow-y-auto scrollbar-hide pr-1 -mr-1 pb-20 h-full">

            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileSelect}
            />

            {/* Upload Area */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h2 className="text-base font-bold text-gray-900 mb-4">Upload Document</h2>
                <div
                    className="border-2 border-dashed border-gray-200 rounded-lg bg-gray-50 flex flex-col items-center justify-center py-12 px-4 text-center hover:bg-gray-50/80 transition-colors cursor-pointer"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                            setSelectedFile(e.dataTransfer.files[0]);
                            setIsUploadModalOpen(true);
                        }
                    }}
                    onClick={triggerFileSelect}
                >
                    <p className="text-sm font-bold text-gray-900 mb-1">Drag your file here to upload</p>
                    <p className="text-xs text-gray-500 mb-4">Supported files: PDF, JPG, PNG, CSV</p>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            triggerFileSelect();
                        }}
                        className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm"
                    >
                        Select files
                        <Upload className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* All Documents List */}
            {loadingDocs ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            ) : (
                <DocumentsTable documents={documents} />
            )}

            {/* Upload Modal (Overlay) */}
            {isUploadModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-[600px] flex flex-col animate-in fade-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">Upload documents</h3>
                            <button onClick={() => setIsUploadModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            {/* File Preview */}
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg mb-6 group">
                                <div className="flex items-center gap-3 overflow-hidden">
                                    <div className="w-8 h-8 rounded bg-red-50 flex items-center justify-center text-red-500 shrink-0">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold text-gray-900 truncate">{selectedFile ? selectedFile.name : 'Unknown File'}</div>
                                        <div className="text-xs text-gray-500 flex items-center gap-1.5">
                                            {selectedFile ? `${(selectedFile.size / 1024).toFixed(0)}KB` : ''}
                                            <span className="text-green-600 font-medium flex items-center gap-0.5">
                                                <CheckCircle className="w-3 h-3" />
                                                Ready
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={() => setIsUploadModalOpen(false)} className="text-gray-400 hover:text-red-500 p-1">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Business Name Input (Read Only / Display) */}
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Business</label>
                                <div className="w-full h-10 px-3 border rounded-lg bg-gray-100 text-sm text-gray-600 flex items-center">
                                    {business?.name || "Select a business first"}
                                </div>
                            </div>

                            {/* Document Type Radio Group */}
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Type of document</label>
                                <div className="space-y-2">
                                    {['Profit & Loss Statements', 'Balance Sheets', 'Bank Transactions', 'Other'].map((type) => (
                                        <label
                                            key={type}
                                            className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${docType === type
                                                ? 'bg-gray-50 border-gray-300'
                                                : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <span className="text-sm font-medium text-gray-700">{type}</span>
                                            <input
                                                type="radio"
                                                name="docType"
                                                value={type}
                                                checked={docType === type}
                                                onChange={() => setDocType(type)}
                                                className="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900"
                                            />
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    onClick={handleUpload}
                                    disabled={uploadProgress || !business?.slug}
                                    className="bg-gray-800 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {uploadProgress ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Uploading...
                                        </>
                                    ) : (
                                        'Upload'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {isSuccessModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-[400px] flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in-95 duration-200 relative">
                        <button onClick={() => setIsSuccessModalOpen(false)} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors">
                            <X className="w-5 h-5" />
                        </button>

                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-200">
                            <CheckCircle className="w-8 h-8 text-white" />
                        </div>

                        <h3 className="text-lg font-bold text-gray-900 text-center leading-snug">
                            Your document have been<br />uploaded successfully.
                        </h3>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Documents;
